// core/store/slices/gridSlice.ts
import { StateCreator } from "zustand";
import {
  GridItem,
  createEmptyGrid,
  updateGridItemAtPosition,
  swapGridItems,
} from "@/domains/Grid/types/grid.types";
import { Post } from "@/core/types/postTypes";

export interface GridSlice {
  // Grid state
  items: GridItem[];
  selectedItemId: string | null;
  isLoading: boolean;
  error: string | null;
  currentGridMode: "live" | "experimental" | "ai-suggest";
  activeGridId: string | number | null;

  // Grid actions
  updateGridItem: (position: number, updates: Partial<GridItem>) => void;
  setSelectedItem: (id: string | null) => void;
  clearGrid: () => void;
  loadGrid: (items: GridItem[]) => void;
  setGridMode: (mode: "live" | "experimental" | "ai-suggest") => void;
  setActiveGridId: (id: string | number | null) => void;
  setGridLoading: (loading: boolean) => void;
  setGridError: (error: string | null) => void;
  swapGridPositions: (sourcePosition: number, targetPosition: number) => void;
  assignNewPostId: (position: number) => void;
  updatePostInGrid: (post: Post) => void;
}

export const createGridSlice: StateCreator<GridSlice> = (set, get) => ({
  // Initial state
  items: createEmptyGrid(12),
  selectedItemId: null,
  isLoading: false,
  error: null,
  currentGridMode: "live",
  activeGridId: null,

  // Actions
  updateGridItem: (position: number, updates: Partial<GridItem>) => {
    set((state) => ({
      items: updateGridItemAtPosition(state.items, position, updates),
    }));
  },

  setSelectedItem: (id: string | null) => {
    set({ selectedItemId: id });
  },

  clearGrid: () => {
    set({
      items: createEmptyGrid(12),
      selectedItemId: null,
      error: null,
    });
  },

  loadGrid: (items: GridItem[]) => {
    set({
      items: items.length > 0 ? items : createEmptyGrid(12),
      selectedItemId: null,
      error: null,
    });
  },

  setGridMode: (mode: "live" | "experimental" | "ai-suggest") => {
    set({
      currentGridMode: mode,
      selectedItemId: null,
    });
  },

  setActiveGridId: (id: string | number | null) => {
    set({ activeGridId: id });
  },

  setGridLoading: (loading: boolean) => {
    set({ isLoading: loading });
  },

  setGridError: (error: string | null) => {
    set({ error });
  },

  swapGridPositions: (sourcePosition: number, targetPosition: number) => {
    set((state) => ({
      items: swapGridItems(state.items, sourcePosition, targetPosition),
    }));
  },

  assignNewPostId: (position: number) => {
    const state = get();
    const item = state.items.find((item) => item.position === position);

    if (item && (!item.post || item.isEmpty)) {
      const newId = `grid-item-${Date.now()}-${position}`;
      state.updateGridItem(position, {
        id: newId,
        isEmpty: false,
        createdAt: new Date().toISOString(),
      });
    }
  },

  updatePostInGrid: (post: Post) => {
    set((state) => {
      const idx = state.items.findIndex((item) => item.post?.id === post.id);
      if (idx === -1) return state;
      const updated = [...state.items];
      updated[idx] = { ...updated[idx], post };
      return { items: updated };
    });
  },
});
